import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import EvaluationAnswerComposer from './EvaluationAnswerComposer';
import EvaluationSubmissionItemDisplay from './display/EvaluationSubmissionItemDisplay';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import CompetencyDisplay from './display/CompetencyDisplay';
import PlaceholderDisplay from './display/PlaceholderDisplay';
import Delta from 'quill-delta';

const Bubble = styled.div<{
	alternate?: boolean;
}>`
	flex-shrink: 1;
	background: ${(props) => (props.alternate ? '#E5F2FB' : 'white')};
	border-radius: 4px;
	padding: 10px;
	min-width: 50%;
`;

const Name = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const Anchor = styled.div`
	color: ${(props) => props.theme.colors.primary};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

export default function EvaluationSubmissionAnswer(props: {
	section: number;
	item: number;
	metadata?: any;
	submission: graphql.EvaluationSubmission;
	party: graphql.EvaluationSubmissionParty;
	relevant: boolean;
	print?: boolean;
	save: () => Promise<void>;
}) {
	const { t } = useTranslation();

	const result = props.submission[props.party.toLowerCase() as 'employee' | 'manager'];

	const alternate = props.party === graphql.EvaluationSubmissionParty.Manager;

	const [edit, setEdit] = useState(false);

	const [saving, setSaving] = useState(false);

	const onSave = async () => {
		setSaving(true);
		await props.save();
		setSaving(false);
	};

	if (edit) {
		return (
			<Box paddingY={2}>
				<EvaluationAnswerComposer
					submission={props.submission}
					section={props.section}
					item={props.item}
					metadata={props.metadata}
					result={result}
					onSave={onSave}
					onDismiss={() => setEdit(false)}
				/>
			</Box>
		);
	}

	const item = props.submission.evaluationForm.sections[props.section].items[props.item];

	const answer = props.submission.viewer.sections[props.section].items[props.item];

	const filledQuillDelta = (delta?: Delta) => delta && delta.ops
		.map((op: { insert?: string }) => op.insert ?? '')
		.join('')
		.trim().length > 0;
	
	const filledIn =
		filledQuillDelta(answer?.openQuestion) ||
		(answer?.competency ?? undefined) !== undefined ||
		(answer?.stars ?? undefined) !== undefined ||
		(answer?.sentiment ?? undefined) !== undefined ||
		filledQuillDelta(answer?.reviewGoals?.find(({ goalID }) => goalID === props.metadata.goal.goal.id)?.text);

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		const employee =
			result.party === graphql.EvaluationSubmissionParty.Employee &&
			[graphql.EvaluationFillInBy.Employee, graphql.EvaluationFillInBy.Both].includes(
				item.fillInBy,
			);

		const manager =
			result.party === graphql.EvaluationSubmissionParty.Manager &&
			[graphql.EvaluationFillInBy.Manager, graphql.EvaluationFillInBy.Both].includes(item.fillInBy);

		return (
			<>
				{saving ? (
					<Box my={3}>
						<Spinner isLoading size="18px" verticalMargin={0} />
					</Box>
				) : (
					<Flex alignItems="center">
						<Box flexGrow={0} flexShrink={1}>
							<CompetencyDisplay
								submission={props.submission}
								section={props.section}
								item={props.item}
							/>
						</Box>
						<Box flexGrow={1} />
						{result.viewerCanUpdate && (employee || manager) && !props.print && (
							<Box ml={10} flexShrink={0}>
								<Anchor onClick={() => setEdit(true)}>
									{filledIn
										? t('evaluation.submission.editAnswer')
										: t('evaluation.submission.enterAnswer')}
								</Anchor>
							</Box>
						)}
					</Flex>
				)}
			</>
		);
	} else if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		const { goal } = props.metadata;

		const index = answer?.reviewGoals?.findIndex(({ goalID }) => goalID === goal.goal.id);
		const enabled = index !== undefined && index >= 0;

		if (!enabled && props.submission.viewer.party === props.party) {
			return null;
		}
	}

	return (
		<Flex alignItems="center">
			<Flex
				paddingY={2}
				flexDirection={alternate ? 'row-reverse' : 'row'}
				flexGrow={1}
				flexShrink={1}
			>
				<Box flexShrink={0} flexGrow={0}>
					<PersonAvatar person={result.user} size={PersonAvatarSize.Medium} />
				</Box>
				<Box flexShrink={0} flexGrow={0} width={10} />
				<Bubble alternate={alternate}>
					<Name>
						{result.user.firstName} {result.user.lastName}
					</Name>
					{saving ? (
						<Box my={2}>
							<Spinner isLoading size="18px" verticalMargin={0} />
						</Box>
					) : props.relevant ? (
						<EvaluationSubmissionItemDisplay
							form={item}
							item={result.sections[props.section].items[props.item]!}
							metadata={props.metadata}
						/>
					) : (
						<PlaceholderDisplay
							text={t(`evaluation.submission.irrelevant.short.${item.fillInBy.toLowerCase()}`)}
						/>
					)}
				</Bubble>
				<Box flexGrow={1} />
			</Flex>
			{result.viewerCanUpdate && props.relevant && !props.print && (
				<Box ml={10} flexShrink={0}>
					<Anchor onClick={() => setEdit(true)}>
						{filledIn
							? t('evaluation.submission.editAnswer')
							: t('evaluation.submission.enterAnswer')}
					</Anchor>
				</Box>
			)}
		</Flex>
	);
}
